import { useState } from "react";
import { createOrg, listOrgMembers, inviteMember } from "../api/enterprise.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

type Org = {
  id: string;
  name: string;
  slug: string;
  planTier: string;
  ownerId: string;
  createdAt: string;
};
type Member = { id: string; userId: string; role: string; joinedAt: string };

export function AdminOrgsPage() {
  const { token } = useAuth();
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", ownerId: "" });
  const [inviteForm, setInviteForm] = useState({ email: "", role: "MEMBER", invitedBy: "" });

  async function loadMembers(orgId: string) {
    const res = (await listOrgMembers(token, orgId)) as Member[];
    setMembers(res ?? []);
    setSelectedOrg(orgId);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const org = (await createOrg(token, form)) as Org;
      setOrgs((prev) => [...prev, org]);
    } finally {
      setLoading(false);
    }
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedOrg) return;
    await inviteMember(token, selectedOrg, inviteForm);
    setInviteForm({ email: "", role: "MEMBER", invitedBy: inviteForm.invitedBy });
  }

  return (
    <Layout>
      <div className="space-y-6 p-6">
        <h1 className="text-2xl font-bold text-white">Organizations</h1>

        <div className="rounded-lg border border-gray-800 bg-gray-900 p-5">
          <h2 className="mb-4 text-lg font-semibold text-white">Create Organization</h2>
          <form onSubmit={handleCreate} className="flex flex-wrap gap-3">
            <input
              className="min-w-40 flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
              placeholder="Organization name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              required
            />
            <input
              className="min-w-40 flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
              placeholder="Owner user ID"
              value={form.ownerId}
              onChange={(e) => setForm((p) => ({ ...p, ownerId: e.target.value }))}
              required
            />
            <button
              type="submit"
              disabled={loading}
              className="rounded bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              Create
            </button>
          </form>
        </div>

        {orgs.length > 0 && (
          <div className="rounded-lg border border-gray-800 bg-gray-900 p-5">
            <h2 className="mb-4 text-lg font-semibold text-white">Organizations</h2>
            <table className="w-full text-sm text-gray-300">
              <thead>
                <tr className="border-b border-gray-800 text-left text-gray-500">
                  <th className="pb-2">Name</th>
                  <th className="pb-2">Slug</th>
                  <th className="pb-2">Plan</th>
                  <th className="pb-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orgs.map((org) => (
                  <tr key={org.id} className="border-b border-gray-800 last:border-0">
                    <td className="py-2 font-medium text-white">{org.name}</td>
                    <td className="py-2 text-indigo-400">{org.slug}</td>
                    <td className="py-2">
                      <span className="rounded bg-indigo-900 px-2 py-0.5 text-xs text-indigo-200">
                        {org.planTier}
                      </span>
                    </td>
                    <td className="py-2">
                      <button
                        onClick={() => loadMembers(org.id)}
                        className="text-xs text-indigo-400 hover:underline"
                      >
                        Members
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selectedOrg && (
          <div className="space-y-4 rounded-lg border border-gray-800 bg-gray-900 p-5">
            <h2 className="text-lg font-semibold text-white">Members</h2>
            {members.map((m) => (
              <div key={m.id} className="flex items-center justify-between text-sm">
                <span className="text-gray-300">{m.userId}</span>
                <span className="rounded bg-gray-800 px-2 py-0.5 text-xs text-gray-400">
                  {m.role}
                </span>
              </div>
            ))}
            <form onSubmit={handleInvite} className="flex flex-wrap gap-3 pt-2">
              <input
                className="min-w-40 flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
                placeholder="Email to invite"
                value={inviteForm.email}
                onChange={(e) => setInviteForm((p) => ({ ...p, email: e.target.value }))}
                required
              />
              <select
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
                value={inviteForm.role}
                onChange={(e) => setInviteForm((p) => ({ ...p, role: e.target.value }))}
              >
                {["OWNER", "ADMIN", "MEMBER", "VIEWER"].map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <input
                className="min-w-40 flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
                placeholder="Invited by user ID"
                value={inviteForm.invitedBy}
                onChange={(e) => setInviteForm((p) => ({ ...p, invitedBy: e.target.value }))}
                required
              />
              <button
                type="submit"
                className="rounded bg-green-700 px-4 py-2 text-white hover:bg-green-800"
              >
                Invite
              </button>
            </form>
          </div>
        )}
      </div>
    </Layout>
  );
}
